import React, { useEffect, useState } from 'react';
import type { ProColumns } from '@ant-design/pro-components';
import { EditableProTable } from '@ant-design/pro-components';
import {
  Space,
  Button,
  message,
  Row,
  Typography,
  Col,
  Skeleton,
  Grid,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { t } from 'i18next';
import {
  createEntryApi,
  deleteEntryApi,
  finalizeWorkLogApi,
  getRejectNoteApi,
  getWorkLogEntriesApi,
  updateEntryApi,
} from '../../APIcalls/APICallsForWorkReportEntry';
import type { InternshipWorkLogEntries } from '../../Types/interfaces';
import { Roles } from '../../Types/interfaces';
import { updateReportApi } from '../../APIcalls/APICallsForReview';
import { v4 as uuidv4 } from 'uuid';
import HelpIcon from '../../Assets/Images/HelpIcon.svg';
import FileUploader from '../FileUploader';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { FaRegFilePdf } from 'react-icons/fa';
import { theme } from 'antd';
import { formatDate } from '../../Utils/date';

const { useBreakpoint } = Grid;

type DataSourceType = {
  id: React.Key;
  startTime?: string;
  endTime?: string;
  review?: string;
  hours?: number;
  description?: string;
  workLogId?: number;
  isNew?: boolean;
};

interface WorkReportTableProps {
  StudentId?: number | string;
  ProcessId?: number | string;
  WorkLogId: number | string;
  WorkLogDocumentUrl: string;
  WorkLogDocumentStatus?: string | Promise<string>;
  editable?: boolean;
  onSubmit: boolean;
  signable: boolean;
  alreadySigned?: boolean;
  workReport?: File | null;
  onSubmitSuccess?: () => void | Promise<void>;
  setWorkReport?(value: File | null): void;
  role: Roles;
  onFileUploadSuccess?: () => Promise<void>;
}

const WorkReportTable = ({
  StudentId,
  ProcessId,
  WorkLogId,
  WorkLogDocumentUrl,
  WorkLogDocumentStatus,
  editable = false,
  onSubmit,
  signable = false,
  alreadySigned,
  workReport,
  onSubmitSuccess,
  setWorkReport,
  role,
}: WorkReportTableProps): React.ReactElement => {
  const [editableRowKeys, setEditableRowKeys] = useState<React.Key[]>([]);
  const [dataSource, setDataSource] = useState<readonly DataSourceType[]>([]);
  const [hasReview, setHasReview] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();
  const loggedInUser = useCurrentUser();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const queryClient = useQueryClient();
  const screens = useBreakpoint();
  const { token } = theme.useToken();

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const { data: workLogs } = await axios.get(
          getWorkLogEntriesApi(WorkLogId)
        );
        const reviewPromises = workLogs.map(
          async (log: InternshipWorkLogEntries) => {
            const { data: reviewResponse } = await axios.get(
              getRejectNoteApi(WorkLogId, log.id)
            );
            return {
              id: log.id,
              text: reviewResponse.text ?? '',
            };
          }
        );
        const reviews = await Promise.all(reviewPromises);

        const updatedDataSource = workLogs
          .map((log: InternshipWorkLogEntries, index: number) => ({
            id: log.id,
            startTime: formatDate(log.startTime),
            endTime: formatDate(log.endTime),
            hours: log.hours,
            description: log.description,
            review: reviews[index].text ?? '',
            key: log.id,
          }))
          .reverse();

        setDataSource(updatedDataSource);
        setHasReview(reviews.some((review) => !!review.text));
      } catch (error) {
        console.error(error);
        messageApi.error(t('workReportComp.fetchError'));
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [WorkLogId, messageApi]);

  const initialStartDate: Date = new Date();
  const initialEndDate: Date = new Date();

  const isColumnEditable = (): boolean => editable && role === Roles.Student;

  const isVisibleReviewColumn = (): boolean =>
    hasReview || role === Roles.Tutor;

  const isVisibleActionsColumn = (): boolean =>
    (role === Roles.Student || role === Roles.Tutor) && editable;

  const handleStudentSaveAction = async (
    data: DataSourceType
  ): Promise<void> => {
    const newData = {
      ...data,
      startTime: formatDate(data.startTime),
      endTime: formatDate(data.endTime),
    };

    if (data.isNew) {
      const { data: response } = await axios.post(createEntryApi(WorkLogId), {
        ...newData,
        workLogId: WorkLogId,
      });
      newData.id = response.id;
      newData.isNew = false;
      setDataSource([newData, ...dataSource]);
      messageApi.success(t('workReportComp.entryAdded'));
    } else {
      const response = await axios.patch(
        updateEntryApi(WorkLogId, newData.id),
        newData
      );
      const data = response.data;

      data.startTime = formatDate(data.startTime);
      data.endTime = formatDate(data.endTime);

      setDataSource(
        dataSource.map((item) =>
          item.id === newData.id ? { ...item, ...data } : item
        )
      );
      messageApi.success(t('workReportComp.entryUpdated'));
    }
  };

  const handleTutorSaveAction = async (data: DataSourceType): Promise<void> => {
    await axios.put(updateReportApi(WorkLogId, data.id), {
      text: data.review,
    });
    setDataSource(
      dataSource.map((item) => {
        if (item.id === data.id) {
          return {
            ...item,
            ...data,
          };
        }
        return item;
      })
    );
    messageApi.success(t('workReportComp.reviewUpdated'));
  };

  const handleSave = async (
    _rowKey: React.Key | React.Key[],
    data: DataSourceType
  ): Promise<void> => {
    try {
      if (role === Roles.Student) {
        await handleStudentSaveAction(data);
      } else if (role === Roles.Tutor) {
        await handleTutorSaveAction(data);
      } else {
        messageApi.error(t('workReportComp.roleError'));
      }
    } catch (error) {
      console.error(error);
      messageApi.error(t('workReportComp.saveError'));
    }
  };

  const handleDelete = async (
    _rowKey: React.Key | React.Key[],
    data: DataSourceType
  ): Promise<void> => {
    try {
      await axios.delete(deleteEntryApi(WorkLogId, data.id));
      setDataSource(dataSource.filter((item) => item.id !== data.id));
      messageApi.success(t('workReportComp.entryDeleted'));
    } catch (error) {
      console.error(error);
      messageApi.error(t('workReportComp.deleteError'));
    }
  };

  const submitWorkReport = async (): Promise<void> => {
    try {
      if (!StudentId || !ProcessId) {
        return;
      }
      await axios.post(finalizeWorkLogApi(StudentId, ProcessId));
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.STUDENT_DATA,
      });
      messageApi.success(t('workReportComp.finalizedWorkReport'));

      if (onSubmitSuccess) {
        await onSubmitSuccess();
      }
    } catch (error) {
      console.error(error);
      messageApi.error(t('workReportComp.finalizeError'));
    }
  };

  const columns: ProColumns<DataSourceType>[] = [
    {
      key: 'startTime',
      title: t('workReportComp.startTime'),
      dataIndex: 'startTime',
      valueType: 'date',
      width: screens.xs ? '90px' : '10%',
      fieldProps: {
        size: screens.xs ? 'small' : 'middle',
        style: {
          width: '120px',
        },
      },
      initialValue: initialStartDate,
      editable: isColumnEditable,
    },
    {
      key: 'endTime',
      title: t('workReportComp.endTime'),
      dataIndex: 'endTime',
      valueType: 'date',
      width: screens.xs ? 90 : 90,
      fieldProps: {
        size: screens.xs ? 'small' : 'middle',
        style: {
          width: '120px',
        },
      },
      initialValue: initialEndDate,
      editable: isColumnEditable,
    },
    {
      key: 'hours',
      title: t('workReportComp.hours'),
      dataIndex: 'hours',
      valueType: 'digit',
      width: screens.xs ? 50 : 50,
      fieldProps: {
        size: screens.xs ? 'small' : 'middle',
        style: {
          width: '60px',
        },
      },
      editable: isColumnEditable,
    },
    {
      key: 'description',
      title: t('workReportComp.desc'),
      dataIndex: 'description',
      valueType: 'textarea',
      width: screens.xs ? 200 : 300,
      fieldProps: {
        size: screens.xs ? 'small' : 'middle',
        style: {
          width: screens.xs ? 180 : 400,
        },
      },
      editable: isColumnEditable,
    },
    {
      key: 'review',
      title: t('workReportComp.review'),
      dataIndex: 'review',
      valueType: 'textarea',
      fieldProps: { placeholder: '' },
      width: screens.xs ? 50 : 50,
      hideInTable: !isVisibleReviewColumn(),
      editable: (): boolean => {
        return role === Roles.Tutor;
      },
    },
    {
      key: 'actions',
      title: t('workReportComp.actions'),
      valueType: 'option',
      width: screens.xs ? 50 : 50,
      hideInTable: !isVisibleActionsColumn(),
      render: (_: unknown, record: DataSourceType) => [
        <Button
          key={`edit-${record.id}`}
          type="primary"
          icon={<EditOutlined />}
          disabled={!editable}
          onClick={() =>
            setEditableRowKeys((prevKeys) => [...prevKeys, record.id])
          }
        />,
        role === Roles.Student && (
          <Button
            key={`delete-${record.id}`}
            type="primary"
            danger
            icon={<DeleteOutlined />}
            disabled={!editable}
            onClick={() => handleDelete(record.id, record)}
          />
        ),
      ],
    },
  ];

  if (isLoading) {
    return (
      <div>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <Row gutter={[0, 20]}>
        <Col span={24}>
          <Space direction="vertical" style={{ display: 'block' }}>
            <EditableProTable<DataSourceType>
              rowKey="id"
              maxLength={50}
              tableLayout="fixed"
              size={screens.lg ? 'middle' : 'small'}
              scroll={{ x: 'max-content' }}
              pagination={{
                defaultPageSize: 10,
                position: ['bottomCenter'],
                showSizeChanger: true,
                pageSizeOptions: ['5', '10', '15', '30', '50', '100', '200'],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              recordCreatorProps={
                isColumnEditable()
                  ? {
                      position: 'top',
                      creatorButtonText: t('workReportComp.addEntry'),
                      record: () => ({
                        id: uuidv4(),
                        isNew: true,
                      }),
                    }
                  : false
              }
              columns={columns}
              value={dataSource}
              editable={{
                type: 'multiple',
                editableKeys: editableRowKeys,
                saveText: <Button icon={<SaveOutlined />} type="primary" />,
                deleteText: (
                  <Button icon={<DeleteOutlined />} danger type="primary" />
                ),
                cancelText: <Button icon={<CloseOutlined />} type="primary" />,
                actionRender: (
                  _row: DataSourceType,
                  _config: unknown,
                  dom: { save: React.ReactNode; cancel: React.ReactNode }
                ) => [dom.save, dom.cancel],
                onSave: handleSave,
                onDelete: handleDelete,
                onChange: setEditableRowKeys,
              }}
            />
          </Space>
        </Col>
        <Col span={24}>
          <div
            style={{
              backgroundColor: token.colorInfoBg,
              border: `1px solid ${token.colorInfoBorder}`,
              borderRadius: token.borderRadiusLG,
              padding: '16px 24px',
              marginBottom: '24px',
            }}
          >
            {/* Title */}
            <Typography.Text
              strong
              style={{
                display: 'block',
                fontSize: '16px',
                marginBottom: '16px',
              }}
            >
              {t('workReportComp.resourcesAndTemplates')}
            </Typography.Text>

            {/* Buttons and links horizontal align */}
            <Space size="large" wrap>
              {/* PDF Download section */}
              <Space size="small" align="center">
                <Button
                  disabled={!WorkLogDocumentUrl}
                  href={WorkLogDocumentUrl}
                  target="_blank"
                  style={{
                    fontSize: 32,
                    padding: 0,
                    height: 'auto',
                    border: 'none',
                    background: 'transparent',
                    color: !WorkLogDocumentUrl ? undefined : token.colorPrimary,
                  }}
                  icon={<FaRegFilePdf />}
                />
                <Typography.Link
                  disabled={!WorkLogDocumentUrl}
                  href={`${WorkLogDocumentUrl}?status=${WorkLogDocumentStatus}`}
                  target="_blank"
                  style={{ fontWeight: 500, fontSize: '15px' }}
                >
                  {t('workReportComp.viewDoc')}
                </Typography.Link>
              </Space>

              {/* Sample section */}
              <Space size="small" align="center" style={{ marginLeft: '16px' }}>
                <a
                  href={process.env.REACT_APP_WORKLOG_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <img
                    src={HelpIcon}
                    alt={t('workReportComp.sampleWorklog')}
                    style={{
                      width: 36,
                      height: 36,
                      objectFit: 'contain',
                      opacity: 0.8,
                    }}
                  />
                </a>
                <Typography.Link
                  href={process.env.REACT_APP_WORKLOG_URL}
                  target="_blank"
                  style={{ fontWeight: 500, fontSize: '15px' }}
                >
                  {t('workReportComp.sampleWorklog')}
                </Typography.Link>
              </Space>
            </Space>
          </div>
        </Col>
      </Row>

      {editable &&
        role === Roles.Student &&
        !signable &&
        dataSource.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <Typography.Title level={5}>
              {t('workReportComp.submitWorkReport')}
            </Typography.Title>
            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginBottom: 10 }}
            >
              {t('workReportComp.additionalWarning')}
            </Typography.Text>
            <Button
              type="primary"
              onClick={onSubmit ? submitWorkReport : undefined}
            >
              {t('workReportComp.submitWorkReport')}
            </Button>
          </div>
        )}
      {signable && workReport !== undefined && setWorkReport && (
        <FileUploader
          apiUrl={`users/${loggedInUser.id}/worklogs/upload-signed`}
          documentUrl={WorkLogDocumentUrl}
          documentSigningStatus={WorkLogDocumentStatus}
          isSignatureDisabled={false}
          alreadySigned={alreadySigned ? alreadySigned : false}
          onFileUpload={async () => {
            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.STUDENT_INTERNSHIP_PROCESSES,
              }),
              queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_BY_USER_ID,
              }),
            ]);
          }}
        />
      )}
    </>
  );
};

export default WorkReportTable;
